import React, { memo, useState } from "react";

import cc from "classcat";
import { useLoadingContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import { Avatar, Popover, Tooltip } from "@narmi/design_system";
import { useFeature } from "byzantine";

import { useCurrentUser } from "../../../../components/contexts/CurrentUserContext";
import { requestNavigation } from "../../../utils/navigationGuard";
import { useSidebarContainer } from "../sidebar.hooks";
import PlainWrapper from "../../../../components/PlainWrapper";

import styles from "./SidebarProfileSwitcher.module.scss";
import LinkedProfileList from "./LinkedProfileList";

import type { SidebarProfileSwitcherProps } from "./sidebar.profile.switcher.types";

const SidebarProfileSwitcherButton = memo(
  ({
    deviceRemembered,
    isOpen = false,
  }: {
    deviceRemembered: boolean;
    isOpen?: boolean;
  }) => {
    const { currentUser } = useCurrentUser();
    const { l10n } = useLocalization();
    // If the device is not remembered, disable the popover since there's nothing to switch to, link to, or forget
    const isPopoverDisabled = !deviceRemembered;
    const { isCollapsed } = useSidebarContainer();

    const SidebarProfileSwitcherClasses = cc([
      "button--reset",
      styles["sidebar-profile-switcher"],
      {
        [styles["sidebar-profile-switcher--active"]]:
          isOpen && !isPopoverDisabled,
      },
    ]);

    const publicName =
      currentUser?.user_category === "business"
        ? currentUser.org_name
        : l10n.getString(
            "sidebar-profile-personal-banking",
            null,
            "Personal banking"
          );

    const userFullName: string =
      currentUser === null
        ? l10n.getString("message-loading-user")
        : `${currentUser.first_name} ${currentUser.last_name}`;
    const userInitials: string | undefined =
      currentUser === null
        ? undefined
        : `${currentUser.first_name?.[0] ?? ""}${
            currentUser.last_name?.[0] ?? ""
          }`;

    const ariaLabel = isPopoverDisabled
      ? undefined
      : l10n.getString("aria-label-profile-switcher-menu");

    return (
      <button
        className={SidebarProfileSwitcherClasses}
        disabled={isPopoverDisabled}
        data-testid="sidebar-profile-switcher-button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isPopoverDisabled ? undefined : false}
      >
        <div aria-hidden>
          <Avatar initials={userInitials} label={userFullName} />
        </div>
        <div className={styles["sidebar-profile-switcher-details-container"]}>
          <div className={cc([styles["sidebar-profile-switcher-details"]])}>
            <h4 className="nds-sans">{userFullName}</h4>
            <span>{publicName}</span>
          </div>
          {!isPopoverDisabled ? (
            <span
              className={`narmi-icon-code ${cc([
                styles["sidebar-profile-switcher-icon"],
                {
                  [styles["sidebar-profile-switcher-icon--open"]]: isOpen,
                  [styles["sidebar-profile-switcher-icon--collapsed"]]:
                    isCollapsed,
                },
              ])}`}
              aria-hidden
            />
          ) : null}
        </div>
      </button>
    );
  }
);
SidebarProfileSwitcherButton.displayName = "SidebarProfileSwitcherButton";

const SidebarProfileSwitcher = ({
  deviceIsRemembered,
  isCollapsed,
}: SidebarProfileSwitcherProps) => {
  const { l10n } = useLocalization();
  const { setIsLoading } = useLoadingContext();
  const { featureEnabled } = useFeature();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isProfileSwitcherFeatureEnabled = featureEnabled("profile_switcher");
  // If we have a remembered account & enable profile switching, allow linking another account
  const isProfileSwitcherEnabled =
    isProfileSwitcherFeatureEnabled && deviceIsRemembered;

  const sidebarProfileSwitcherContainerClasses = cc([
    styles["sidebar-profile-switcher-popover-container"],
    isCollapsed &&
      styles["sidebar-profile-switcher-popover-container--collapsed"],
  ]);

  const Wrapper = isCollapsed ? Tooltip : PlainWrapper;

  return (
    <div className={sidebarProfileSwitcherContainerClasses}>
      <Popover
        matchTriggerWidth={!isCollapsed}
        wrapperDisplay="block"
        isOpen={isPopoverOpen}
        onUserDismiss={() => setIsPopoverOpen(false)}
        onUserEnable={() => setIsPopoverOpen(true)}
        renderTrigger={(isOpen: any) => (
          <Wrapper
            text={l10n.getString("sidebar-collapsed-avatar-tooltip")}
            side="right"
            wrapperDisplay="block"
          >
            <SidebarProfileSwitcherButton
              deviceRemembered={deviceIsRemembered}
              isOpen={isOpen}
            />
          </Wrapper>
        )}
        content={
          <div
            data-testid="sidebar-profile-switcher-profile-list"
            className={cc([styles["sidebar-profile-switcher-popover"]])}
            role="menu"
            aria-label={l10n.getString("aria-label-profile-switcher-menu")}
          >
            <LinkedProfileList deviceRemembered={deviceIsRemembered} />
            {deviceIsRemembered ? (
              <button
                className={cc([
                  styles["sidebar-profile-switcher-popover-menu-item"],
                ])}
                onClick={() => {
                  setIsPopoverOpen(false);
                  requestNavigation(() =>
                    window.open("/logout/forget", "_self")
                  );
                }}
                data-testid="forget-this-device-button"
                role="menuitem"
                aria-label={l10n.getString(
                  "topnav-forget-this-device",
                  null,
                  "Forget current profile"
                )}
              >
                {l10n.getString(
                  "topnav-forget-this-device",
                  null,
                  "Forget current profile"
                )}
              </button>
            ) : null}
            {isProfileSwitcherEnabled ? (
              <>
                <button
                  className={cc([
                    styles["sidebar-profile-switcher-popover-menu-item"],
                  ])}
                  onClick={() => {
                    setIsPopoverOpen(false);
                    requestNavigation(() => window.open("/logout", "_self"));
                  }}
                  data-testid="profile-switcher-logout-button"
                  role="menuitem"
                  aria-label={l10n.getString("topnav-logout")}
                >
                  {l10n.getString("topnav-logout")}
                </button>

                <button
                  className={cc([
                    styles["sidebar-profile-switcher-popover-link-profile"],
                  ])}
                  data-testid="link-profile-button"
                  role="menuitem"
                  aria-label={l10n.getString(
                    "topnav-link-profile-add",
                    null,
                    "Link another profile"
                  )}
                  onClick={() => {
                    setIsPopoverOpen(false);
                    requestNavigation(() => {
                      setIsLoading(true);
                      window.open("/login/link", "_self");
                    });
                  }}
                >
                  <span className="narmi-icon-plus" aria-hidden />
                  {l10n.getString(
                    "topnav-link-profile-add",
                    null,
                    "Link another profile"
                  )}
                </button>
              </>
            ) : null}
          </div>
        }
      />
    </div>
  );
};

export default memo(SidebarProfileSwitcher);
